import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { Search, Plus, ClipboardList, Building2, FileSignature, UserPlus, X, CornerDownLeft } from "lucide-react"
import { useAuthStore } from "@/store/authStore"
import { useUiStore } from "@/store/uiStore"
import carteraService from "@/services/carteraService"
import cd2bPropuestaService from "@/services/cd2bPropuestaService"
import { cn } from "@/lib/utils"
import { navFor, todosLosItems } from "./nav/navConfig"

interface Resultado {
  grupo: string
  texto: string
  detalle?: string
  icono: React.ReactNode
  ruta: string
}

const ESTADO_COT: Record<string, string> = {
  borrador: "Borrador",
  enviada: "Enviada",
  aprobada: "Aceptada · sin pagar",
  pagada: "Pagada",
  activada: "En producción",
  rechazada: "Pidió cambios",
  vencida: "Vencida",
  cancelada: "Cancelada",
}

const sinAcentos = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()

/**
 * Buscador global (Ctrl K, o la lupa del encabezado en el teléfono): pantallas, acciones y, para el
 * asesor cd-2b, sus empresas y cotizaciones. Las cotizaciones se piden una vez al abrir y se filtran
 * aquí; las empresas se buscan en la API (`/partners/cartera?q=`) porque la cartera puede ser grande.
 */
export default function CommandPalette() {
  const navigate = useNavigate()
  const partner = useAuthStore((s) => s.partner)
  const abierto = useUiStore((s) => s.buscadorAbierto)
  const setAbierto = useUiStore((s) => s.setBuscador)
  const [q, setQ] = useState("")
  const [sel, setSel] = useState(0)
  const [propuestas, setPropuestas] = useState<any[] | null>(null)
  const [empresas, setEmpresas] = useState<any[]>([])
  const listaRef = useRef<HTMLDivElement>(null)

  const esCd2b = partner?.organizationType === "cd2b_consultant"
  const def = useMemo(() => navFor(partner), [partner])

  // Ctrl/⌘ K desde cualquier pantalla.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault()
        setAbierto(!useUiStore.getState().buscadorAbierto)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [setAbierto])

  useEffect(() => {
    if (!abierto) {
      setQ("")
      setSel(0)
      return
    }
    if (esCd2b && propuestas === null) {
      cd2bPropuestaService
        .getAll()
        .then((r: any) => setPropuestas(Array.isArray(r?.data) ? r.data : []))
        .catch(() => setPropuestas([]))
    }
  }, [abierto, esCd2b, propuestas])

  useEffect(() => {
    if (!abierto || !esCd2b || q.trim().length < 2) {
      setEmpresas([])
      return
    }
    const t = setTimeout(() => {
      carteraService
        .getCartera({ q: q.trim() }, { limit: 6 })
        .then((r: any) => setEmpresas(r?.data?.items || []))
        .catch(() => setEmpresas([]))
    }, 220)
    return () => clearTimeout(t)
  }, [q, abierto, esCd2b])

  const resultados = useMemo<Resultado[]>(() => {
    const n = sinAcentos(q.trim())
    const pasa = (s: string) => !n || sinAcentos(s).includes(n)
    const acciones: Resultado[] = esCd2b
      ? [
          { grupo: "Acciones", texto: "Nueva cotización", icono: <Plus size={17} />, ruta: "/consultor/cotizaciones/nueva" },
          { grupo: "Acciones", texto: "Nuevo levantamiento", icono: <ClipboardList size={17} />, ruta: "/consultor/levantamientos" },
          ...(partner?.rolCd2b === "gerente"
            ? [{ grupo: "Acciones", texto: "Invitar asesor", icono: <UserPlus size={17} />, ruta: "/equipo?invitar=1" }]
            : []),
        ]
      : []
    const pantallas: Resultado[] = todosLosItems(def).map((it) => ({ grupo: "Ir a", texto: it.text, icono: it.icon, ruta: it.path }))
    const cots: Resultado[] = (propuestas || [])
      .filter((p) => n && pasa(`${p.empresaNombre || ""} ${p.cliente?.nombre || ""}`))
      .slice(0, 6)
      .map((p) => ({
        grupo: "Cotizaciones",
        texto: p.empresaNombre || "Cotización sin nombre",
        detalle: ESTADO_COT[p.estadoEfectivo || p.estado] || p.estado,
        icono: <FileSignature size={17} />,
        ruta: `/consultor/cotizaciones/${p._id}`,
      }))
    const emps: Resultado[] = empresas.map((e) => ({
      grupo: "Empresas",
      texto: e.empresaNombre || "Sin nombre",
      detalle: e.dueño?.email || e.giro || undefined,
      icono: <Building2 size={17} />,
      ruta: `/clientes/${e.subscriptionId}`,
    }))
    return [...acciones.filter((r) => pasa(r.texto)), ...cots, ...emps, ...pantallas.filter((r) => pasa(r.texto))]
  }, [q, def, propuestas, empresas, esCd2b, partner?.rolCd2b])

  useEffect(() => setSel(0), [q])
  useEffect(() => {
    listaRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" })
  }, [sel])

  const ir = (r?: Resultado) => {
    if (!r) return
    setAbierto(false)
    navigate(r.ruta)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setSel((s) => Math.min(resultados.length - 1, s + 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setSel((s) => Math.max(0, s - 1))
    } else if (e.key === "Enter") {
      e.preventDefault()
      ir(resultados[sel])
    }
  }

  let grupoPrevio = ""
  return (
    <DialogPrimitive.Root open={abierto} onOpenChange={setAbierto}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-neutral-900/45 dark:bg-black/60 backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
        <DialogPrimitive.Content
          onKeyDown={onKeyDown}
          className={cn(
            "fixed z-50 flex flex-col bg-card text-card-foreground border-line shadow-xl outline-none",
            "inset-x-0 bottom-0 top-[6dvh] rounded-t-2xl border-t data-[state=open]:animate-slide-in-from-bottom",
            "sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-[12vh] sm:w-[min(600px,calc(100vw-2rem))] sm:-translate-x-1/2 sm:rounded-lg sm:border sm:max-h-[70vh] sm:data-[state=open]:animate-fade-in"
          )}
        >
          <DialogPrimitive.Title className="sr-only">Buscar</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">Pantallas, acciones, empresas y cotizaciones</DialogPrimitive.Description>
          <div className="flex items-center gap-3 border-b border-line px-4 h-14 shrink-0">
            <Search size={18} className="text-subtle shrink-0" aria-hidden="true" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={esCd2b ? "Buscar empresa, cotización o pantalla" : "Buscar pantalla"}
              aria-label="Buscar"
              role="combobox"
              aria-expanded="true"
              aria-controls="buscador-resultados"
              className="flex-1 bg-transparent text-body outline-none placeholder:text-subtle border-0 focus:ring-0 p-0"
            />
            <DialogPrimitive.Close className="inline-flex h-9 w-9 items-center justify-center rounded-md text-subtle hover:bg-surface-muted" aria-label="Cerrar">
              <X size={18} />
            </DialogPrimitive.Close>
          </div>
          <div ref={listaRef} id="buscador-resultados" role="listbox" className="flex-1 overflow-y-auto p-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
            {resultados.length === 0 && (
              <p className="px-3 py-10 text-center text-body-sm text-subtle">Nada coincide con «{q}».</p>
            )}
            {resultados.map((r, idx) => {
              const cabecera = r.grupo !== grupoPrevio
              grupoPrevio = r.grupo
              const activo = idx === sel
              return (
                <div key={`${r.grupo}-${r.ruta}-${idx}`}>
                  {cabecera && <p className="px-3 pt-3 pb-1 text-micro uppercase tracking-wider text-subtle">{r.grupo}</p>}
                  <button
                    type="button"
                    role="option"
                    aria-selected={activo}
                    onMouseMove={() => setSel(idx)}
                    onClick={() => ir(r)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-md px-3 min-h-[44px] text-left text-body-sm",
                      activo ? "bg-primary-50 text-primary-800 dark:bg-primary/20 dark:text-primary-100" : "text-foreground"
                    )}
                  >
                    <span className={cn("shrink-0", activo ? "text-primary dark:text-primary-300" : "text-subtle")}>{r.icono}</span>
                    <span className="flex-1 truncate font-medium">{r.texto}</span>
                    {r.detalle && <span className="truncate text-caption text-subtle max-w-[45%]">{r.detalle}</span>}
                    {activo && <CornerDownLeft size={14} className="hidden sm:block text-subtle" aria-hidden="true" />}
                  </button>
                </div>
              )
            })}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
